import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  CalendarDays,
  Globe2,
  LineChart,
  LogOut,
  Package,
  Settings,
  ShoppingCart,
  TrendingUp,
  X,
} from 'lucide-react';
import { getDashboardSales, getMe, listPortalViewerGroups } from '../api.js';
import { logout } from '../auth.js';
import BrandLogo from '../components/BrandLogo.js';
import type {
  ArticleSalesRow,
  ArticleTrendPoint,
  CountryPoint,
  DashboardSalesResponse,
} from '../types.js';

type Granularity = 'day' | 'week' | 'month';
type Locale = 'de-DE' | 'en-US';

interface PeriodFilter {
  from: string;
  to: string;
  label: string;
}

interface DashboardFilterState {
  articleIds: string[];
  channels: string[];
  countries: string[];
  period: PeriodFilter | null;
}

interface DashboardCopy {
  admin: string;
  viewerGroup: string;
  from: string;
  to: string;
  day: string;
  week: string;
  month: string;
  noViewerGroup: string;
  dashboardLoadError: string;
  resetSelection: string;
  salesTrend: string;
  channels: string;
  products: string;
  countries: string;
  previousPeriod: string;
  activeArticles: string;
  topArticle: string;
  noData: string;
  previousUnitsChange: string;
  revenue: string;
  netInPeriod: string;
  notReleased: string;
  filterChannel: string;
  filterCountry: string;
  filterArticle: string;
  filterPeriod: string;
  clearFilters: string;
  noSales: string;
  noDataInPeriod: string;
  noProducts: string;
  customerSkuArticle: string;
  sold: string;
  stock: string;
  cover: string;
  customerSkuMissing: string;
  internalSku: string;
  unknown: string;
  days: string;
  dateRangeSeparator: string;
  salesTrendAria: string;
}

const emptyFilters: DashboardFilterState = {
  articleIds: [],
  channels: [],
  countries: [],
  period: null,
};

const dashboardCopy: Record<Locale, DashboardCopy> = {
  'de-DE': {
    admin: 'Admin',
    viewerGroup: 'Viewer Group',
    from: 'Von',
    to: 'Bis',
    day: 'Tag',
    week: 'Woche',
    month: 'Monat',
    noViewerGroup: 'Deinem User ist noch keine aktive Viewer Group zugeordnet.',
    dashboardLoadError: 'Dashboard konnte nicht geladen werden.',
    resetSelection: 'Auswahl zuruecksetzen',
    salesTrend: 'Sales Trend',
    channels: 'Channels',
    products: 'Produkte',
    countries: 'Laender',
    previousPeriod: 'Vorperiode',
    activeArticles: 'Aktive Artikel im Zeitraum',
    topArticle: 'Top-Artikel',
    noData: 'Keine Daten',
    previousUnitsChange: 'Units-Veraenderung',
    revenue: 'Umsatz',
    netInPeriod: 'Netto im Zeitraum',
    notReleased: 'Nicht freigegeben',
    filterChannel: 'Channel',
    filterCountry: 'Land',
    filterArticle: 'Artikel',
    filterPeriod: 'Zeitraum',
    clearFilters: 'Filter loeschen',
    noSales: 'Keine Sales im gewaehlten Zeitraum.',
    noDataInPeriod: 'Keine Daten im Zeitraum.',
    noProducts: 'Keine Produkte im Zeitraum.',
    customerSkuArticle: 'Kunden-SKU / Artikel',
    sold: 'Verkauft',
    stock: 'Bestand',
    cover: 'Reichweite',
    customerSkuMissing: 'Kunden-SKU fehlt',
    internalSku: 'SKU',
    unknown: 'Unbekannt',
    days: 'Tage',
    dateRangeSeparator: 'bis',
    salesTrendAria: 'Sales Trend nach Artikeln',
  },
  'en-US': {
    admin: 'Admin',
    viewerGroup: 'Viewer Group',
    from: 'From',
    to: 'To',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    noViewerGroup: 'No active viewer group is assigned to your user yet.',
    dashboardLoadError: 'Dashboard could not be loaded.',
    resetSelection: 'Reset selection',
    salesTrend: 'Sales Trend',
    channels: 'Channels',
    products: 'Products',
    countries: 'Countries',
    previousPeriod: 'Previous period',
    activeArticles: 'Active products in period',
    topArticle: 'Top product',
    noData: 'No data',
    previousUnitsChange: 'Units change',
    revenue: 'Revenue',
    netInPeriod: 'Net in period',
    notReleased: 'Not enabled',
    filterChannel: 'Channel',
    filterCountry: 'Country',
    filterArticle: 'Product',
    filterPeriod: 'Period',
    clearFilters: 'Clear filters',
    noSales: 'No sales in the selected period.',
    noDataInPeriod: 'No data in this period.',
    noProducts: 'No products in this period.',
    customerSkuArticle: 'Customer SKU / Product',
    sold: 'Sold',
    stock: 'Stock',
    cover: 'Coverage',
    customerSkuMissing: 'Customer SKU missing',
    internalSku: 'SKU',
    unknown: 'Unknown',
    days: 'days',
    dateRangeSeparator: 'to',
    salesTrendAria: 'Sales trend by product',
  },
};

const chartColors = ['#117f8a', '#6f8f2f', '#c46a2c', '#4059ad', '#b54c6a', '#7c6f64', '#2f7d57', '#8b5fbf'];
let activeDashboardCopy: DashboardCopy = dashboardCopy['de-DE'];

export default function DashboardPage() {
  const defaultRange = useMemo(() => lastDaysRange(90), []);
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [selectedViewerGroupId, setSelectedViewerGroupId] = useState<string | null>(null);
  const [filters, setFilters] = useState<DashboardFilterState>(emptyFilters);

  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const viewerGroups = useQuery({
    queryKey: ['portal-viewer-groups'],
    queryFn: listPortalViewerGroups,
  });

  useEffect(() => {
    if (selectedViewerGroupId) return;
    const first = viewerGroups.data?.viewerGroups[0];
    if (first) setSelectedViewerGroupId(first.id);
  }, [selectedViewerGroupId, viewerGroups.data?.viewerGroups]);

  const activeViewerGroup = viewerGroups.data?.viewerGroups.find(
    (group) => group.id === selectedViewerGroupId,
  );
  const locale: Locale = activeViewerGroup?.settings?.locale === 'en-US' ? 'en-US' : 'de-DE';
  const copy = dashboardCopy[locale];
  activeDashboardCopy = copy;

  const sales = useQuery({
    queryKey: ['dashboard-sales', selectedViewerGroupId, from, to, granularity, filters],
    queryFn: () =>
      getDashboardSales({
        viewerGroupId: selectedViewerGroupId,
        from,
        to,
        granularity,
        limit: 30,
        articleIds: filters.articleIds,
        channels: filters.channels,
        countries: filters.countries,
        periodFrom: filters.period?.from,
        periodTo: filters.period?.to,
      }),
    enabled: Boolean(selectedViewerGroupId),
  });

  const viewerGroupCount = viewerGroups.data?.viewerGroups.length ?? 0;
  const showEmptyGroupState = !viewerGroups.isLoading && viewerGroupCount === 0;

  const filterLabels = useMemo(() => createFilterLabels(sales.data, copy), [sales.data, copy]);

  const toggleArrayFilter = (key: 'articleIds' | 'channels' | 'countries', value: string) => {
    setFilters((current) => ({
      ...current,
      [key]: toggleValue(current[key], value),
    }));
  };

  const togglePeriod = (period: string) => {
    const next = periodFilter(period, granularity, locale, copy);
    setFilters((current) => ({
      ...current,
      period: current.period?.from === next.from && current.period?.to === next.to ? null : next,
    }));
  };

  const removeFilter = (kind: keyof DashboardFilterState, value?: string) => {
    setFilters((current) => {
      if (kind === 'period') return { ...current, period: null };
      return { ...current, [kind]: current[kind].filter((item) => item !== value) };
    });
  };

  const resetDashboardSelection = () => {
    setSelectedViewerGroupId(null);
    setFilters(emptyFilters);
  };

  return (
    <main className="min-h-screen bg-[#f6f7f9] text-slate-950">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-3 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <div className="min-w-0 justify-self-start">
            <div className="flex flex-wrap items-end gap-3">
              <h1 className="text-xl font-semibold">Dashboard</h1>
              {activeViewerGroup ? (
                <span className="pb-0.5 text-sm text-slate-500">{activeViewerGroup.name}</span>
              ) : null}
            </div>
          </div>

          <BrandLogo className="h-5 justify-self-center sm:h-6" />

          <div className="flex min-w-0 flex-wrap items-center justify-start gap-3 sm:justify-end">
            {me.data?.user.isAdmin ? (
              <a
                href="/admin"
                className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <Settings size={16} aria-hidden="true" />
                {copy.admin}
              </a>
            ) : null}
            <span className="min-w-0 truncate text-sm text-slate-600">{me.data?.user.email}</span>
            <button
              type="button"
              title="Logout"
              onClick={logout}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            >
              <LogOut size={17} aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl space-y-5 px-5 py-5">
        <section className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field label={copy.viewerGroup}>
              <select
                value={selectedViewerGroupId ?? ''}
                onChange={(event) => {
                  setSelectedViewerGroupId(event.target.value || null);
                  setFilters(emptyFilters);
                }}
                disabled={!me.data?.user.isAdmin || viewerGroupCount <= 1}
                className="h-10 min-w-[220px] rounded-md border border-slate-300 bg-white px-3 text-sm disabled:bg-slate-100"
              >
                {viewerGroups.data?.viewerGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={copy.from}>
              <input
                type="date"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setFilters((current) => ({ ...current, period: null }));
                }}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
              />
            </Field>
            <Field label={copy.to}>
              <input
                type="date"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setFilters((current) => ({ ...current, period: null }));
                }}
                className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
              />
            </Field>
          </div>

          <div className="inline-grid grid-cols-3 overflow-hidden rounded-md border border-slate-300 bg-white text-sm">
            {(['day', 'week', 'month'] as const).map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setGranularity(value);
                  setFilters((current) => ({ ...current, period: null }));
                }}
                className={`h-10 px-4 font-medium ${
                  granularity === value ? 'bg-[#117f8a] text-white' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {value === 'day' ? copy.day : value === 'week' ? copy.week : copy.month}
              </button>
            ))}
          </div>
        </section>

        <FilterBar
          filters={filters}
          labels={filterLabels}
          copy={copy}
          onRemove={removeFilter}
          onClear={() => setFilters(emptyFilters)}
        />

        {showEmptyGroupState ? (
          <section className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {copy.noViewerGroup}
          </section>
        ) : null}

        {sales.isError ? (
          <section className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {sales.error instanceof Error ? sales.error.message : copy.dashboardLoadError}
            <button type="button" onClick={resetDashboardSelection} className="ml-3 underline">
              {copy.resetSelection}
            </button>
          </section>
        ) : null}

        <KpiGrid
          data={sales.data}
          isLoading={sales.isLoading}
          locale={locale}
          copy={copy}
          onTopArticleClick={(articleId) => toggleArrayFilter('articleIds', articleId)}
          activeArticleIds={filters.articleIds}
        />

        <section className="grid gap-5 xl:grid-cols-[1.45fr_0.9fr]">
          <Panel
            title={copy.salesTrend}
            icon={<LineChart size={17} aria-hidden="true" />}
            aside={`${formatDate(filters.period?.from ?? from, locale)} ${copy.dateRangeSeparator} ${formatDate(filters.period?.to ?? to, locale)}`}
          >
            <StackedArticleBars
              points={sales.data?.articleTrend ?? []}
              granularity={granularity}
              locale={locale}
              copy={copy}
              activePeriod={filters.period}
              activeArticleIds={filters.articleIds}
              colorScopeKey={`${selectedViewerGroupId ?? ''}:${from}:${to}:${granularity}`}
              onTogglePeriod={togglePeriod}
              onToggleArticle={(articleId) => toggleArrayFilter('articleIds', articleId)}
            />
          </Panel>

          <Panel title={copy.channels} icon={<BarChart3 size={17} aria-hidden="true" />}>
            <BarList
              rows={(sales.data?.channels ?? []).map((row) => ({
                key: row.channelGroup,
                label: row.channelLabel,
                value: row.units,
                detail: sales.data?.viewerGroup.showRevenue
                  ? formatRevenue(row.netRevenue, locale)
                  : `${formatUnits(row.units, locale)} Units`,
              }))}
              copy={copy}
              isActive={(key) => filters.channels.includes(key)}
              onToggle={(key) => toggleArrayFilter('channels', key)}
            />
          </Panel>
        </section>

        <section className="grid gap-5 xl:grid-cols-[1.25fr_0.8fr]">
          <Panel title={copy.products} icon={<Package size={17} aria-hidden="true" />}>
            <ArticleTable
              rows={sales.data?.topArticles ?? []}
              locale={locale}
              copy={copy}
              activeArticleIds={filters.articleIds}
              onToggle={(articleId) => toggleArrayFilter('articleIds', articleId)}
            />
          </Panel>

          <Panel title="Länder" icon={<Globe2 size={17} aria-hidden="true" />}>
            <BarList
              rows={(sales.data?.countries ?? []).map((row) => ({
                key: row.countryCode,
                label: countryLabel(row, copy),
                value: row.units,
                detail: `${formatUnits(row.units, locale)} Units`,
              }))}
              copy={copy}
              isActive={(key) => filters.countries.includes(key)}
              onToggle={(key) => toggleArrayFilter('countries', key)}
            />
          </Panel>
        </section>
      </div>
    </main>
  );
}

function KpiGrid({
  data,
  isLoading,
  locale,
  copy,
  onTopArticleClick,
  activeArticleIds,
}: {
  data: DashboardSalesResponse | undefined;
  isLoading: boolean;
  locale: Locale;
  copy: DashboardCopy;
  onTopArticleClick: (articleId: string) => void;
  activeArticleIds: string[];
}) {
  const showRevenue = data?.viewerGroup.showRevenue ?? false;
  const topArticle = data?.kpis.topArticle;
  return (
    <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      <KpiTile
        label="Units"
        value={isLoading ? '...' : formatUnits(data?.kpis.units ?? 0, locale)}
        icon={<ShoppingCart size={18} aria-hidden="true" />}
        detail={`${copy.previousPeriod} ${formatUnits(data?.kpis.previousUnits ?? 0, locale)}`}
      />
      <KpiTile
        label="SKUs"
        value={isLoading ? '...' : formatNumber(data?.kpis.skuCount ?? 0, locale)}
        icon={<Package size={18} aria-hidden="true" />}
        detail={copy.activeArticles}
      />
      <KpiTile
        label={copy.topArticle}
        value={topArticle ? customerSkuDisplay(topArticle, copy) : '-'}
        icon={<TrendingUp size={18} aria-hidden="true" />}
        detail={topArticle ? articleSubtitle(topArticle, copy) : copy.noData}
        active={topArticle ? activeArticleIds.includes(topArticle.articleId) : false}
        onClick={topArticle ? () => onTopArticleClick(topArticle.articleId) : undefined}
      />
      <KpiTile
        label={copy.previousPeriod}
        value={formatPercent(data?.kpis.unitsDeltaPct ?? null, locale)}
        icon={<CalendarDays size={18} aria-hidden="true" />}
        detail="Units-Veränderung"
      />
      <KpiTile
        label={copy.revenue}
        value={showRevenue ? formatRevenue(data?.kpis.netRevenue ?? 0, locale) : '-'}
        icon={<BarChart3 size={18} aria-hidden="true" />}
        detail={showRevenue ? copy.netInPeriod : copy.notReleased}
      />
    </section>
  );
}

function KpiTile({
  label,
  value,
  detail,
  icon,
  active = false,
  onClick,
}: {
  label: string;
  value: string;
  detail: string;
  icon: React.ReactNode;
  active?: boolean;
  onClick?: (() => void) | undefined;
}) {
  const displayDetail =
    detail === 'Units-V\u00c3\u00a4nderung' || detail === 'Units-Ver\u00e4nderung'
      ? activeDashboardCopy.previousUnitsChange
      : detail;
  const content = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</span>
        <span className="text-[#117f8a]">{icon}</span>
      </div>
      <p className="mt-3 truncate text-2xl font-semibold tracking-normal text-slate-950">{value}</p>
      <p className="mt-1 truncate text-xs text-slate-500">{displayDetail}</p>
    </>
  );

  if (!onClick) {
    return <article className="rounded-md border border-slate-200 bg-white p-4">{content}</article>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border p-4 text-left transition hover:border-[#117f8a] hover:bg-[#f1fbfc] ${
        active ? 'border-[#117f8a] bg-[#e5f6f7]' : 'border-slate-200 bg-white'
      }`}
    >
      {content}
    </button>
  );
}

function Panel({
  title,
  icon,
  aside,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  const displayTitle =
    title === 'L\u00c3\u00a4nder' || title === 'L\u00e4nder' ? activeDashboardCopy.countries : title;
  return (
    <section className="rounded-md border border-slate-200 bg-white">
      <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
        <h2 className="inline-flex items-center gap-2 text-sm font-semibold">
          <span className="text-[#117f8a]">{icon}</span>
          {displayTitle}
        </h2>
        {aside ? <div className="text-xs text-slate-500">{aside}</div> : null}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function FilterBar({
  filters,
  labels,
  copy,
  onRemove,
  onClear,
}: {
  filters: DashboardFilterState;
  labels: ReturnType<typeof createFilterLabels>;
  copy: DashboardCopy;
  onRemove: (kind: keyof DashboardFilterState, value?: string) => void;
  onClear: () => void;
}) {
  const chips = [
    ...filters.channels.map((value) => ({
      key: `channels:${value}`,
      kind: 'channels' as const,
      value,
      label: `${copy.filterChannel}: ${labels.channels.get(value) ?? value}`,
    })),
    ...filters.countries.map((value) => ({
      key: `countries:${value}`,
      kind: 'countries' as const,
      value,
      label: `${copy.filterCountry}: ${labels.countries.get(value) ?? value}`,
    })),
    ...filters.articleIds.map((value) => ({
      key: `articleIds:${value}`,
      kind: 'articleIds' as const,
      value,
      label: `${copy.filterArticle}: ${labels.articles.get(value) ?? value}`,
    })),
    ...(filters.period
      ? [
          {
            key: 'period',
            kind: 'period' as const,
            value: undefined,
            label: `${copy.filterPeriod}: ${filters.period.label}`,
          },
        ]
      : []),
  ];

  if (chips.length === 0) return null;

  return (
    <section className="flex flex-wrap items-center gap-2">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={() => onRemove(chip.kind, chip.value)}
          className="inline-flex h-8 items-center gap-2 rounded-md border border-[#117f8a]/30 bg-[#e5f6f7] px-2.5 text-sm text-[#0c6871] hover:bg-[#d6eff1]"
        >
          {chip.label}
          <X size={14} aria-hidden="true" />
        </button>
      ))}
      <button
        type="button"
        onClick={onClear}
        data-label={copy.clearFilters}
        className="inline-flex h-8 items-center rounded-md border border-slate-300 bg-white px-3 text-sm text-transparent before:text-slate-700 before:content-[attr(data-label)] hover:bg-slate-50"
      >
        Filter löschen
      </button>
    </section>
  );
}

function StackedArticleBars({
  points,
  granularity,
  locale,
  copy,
  activePeriod,
  activeArticleIds,
  colorScopeKey,
  onTogglePeriod,
  onToggleArticle,
}: {
  points: ArticleTrendPoint[];
  granularity: Granularity;
  locale: Locale;
  copy: DashboardCopy;
  activePeriod: PeriodFilter | null;
  activeArticleIds: string[];
  colorScopeKey: string;
  onTogglePeriod: (period: string) => void;
  onToggleArticle: (articleId: string) => void;
}) {
  const colorByArticleRef = useRef(new Map<string, string>());
  const colorScopeRef = useRef(colorScopeKey);
  if (colorScopeRef.current !== colorScopeKey) {
    colorScopeRef.current = colorScopeKey;
    colorByArticleRef.current = new Map();
  }

  if (points.length === 0) return <EmptyState text={copy.noSales} />;

  if (points.length === 0) {
    return <EmptyState text="Keine Sales im gewählten Zeitraum." />;
  }

  const width = 780;
  const height = 260;
  const left = 56;
  const right = 12;
  const bottom = 34;
  const top = 12;
  const periods = Array.from(new Set(points.map((point) => point.period))).sort();
  const articleTotals = new Map<string, number>();
  const articleMap = new Map<string, ArticleTrendPoint>();
  for (const point of points) {
    articleMap.set(point.articleId, point);
    articleTotals.set(point.articleId, (articleTotals.get(point.articleId) ?? 0) + point.units);
  }
  const articles = [...articleMap.values()].sort(
    (leftArticle, rightArticle) =>
      (articleTotals.get(rightArticle.articleId) ?? 0) - (articleTotals.get(leftArticle.articleId) ?? 0),
  );
  const articleColorById = new Map<string, string>();
  for (const [index, article] of articles.entries()) {
    const existingColor = colorByArticleRef.current.get(article.articleId);
    const color = existingColor ?? chartColor(index);
    colorByArticleRef.current.set(article.articleId, color);
    articleColorById.set(article.articleId, color);
  }
  const periodTotals = periods.map((period) =>
    points.filter((point) => point.period === period).reduce((sum, point) => sum + point.units, 0),
  );
  const chartMax = niceChartMax(Math.max(1, ...periodTotals));
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const slot = chartWidth / Math.max(1, periods.length);
  const barWidth = Math.max(8, Math.min(34, slot * 0.68));
  const ticks = buildTicks(chartMax);
  const unitsByPeriodArticle = new Map(
    points.map((point) => [`${point.period}:${point.articleId}`, point.units]),
  );

  const bars = periods.flatMap((period, periodIndex) => {
    let used = 0;
    const x = left + periodIndex * slot + (slot - barWidth) / 2;
    return articles.map((article) => {
      const units = unitsByPeriodArticle.get(`${period}:${article.articleId}`) ?? 0;
      const segmentHeight = (units / chartMax) * chartHeight;
      const y = top + chartHeight - ((used + units) / chartMax) * chartHeight;
      used += units;
      return {
        key: `${period}:${article.articleId}`,
        period,
        article,
        x,
        y,
        width: barWidth,
        height: Math.max(0, segmentHeight),
        color: articleColorById.get(article.articleId) ?? chartColor(0),
      };
    });
  });

  return (
    <div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={copy.salesTrendAria} className="h-72 w-full">
        {ticks.map((tick) => {
          const y = top + chartHeight - (tick / chartMax) * chartHeight;
          return (
            <g key={tick}>
              <line x1={left} y1={y} x2={width - right} y2={y} stroke="#e2e8f0" />
              <text x={left - 8} y={y + 4} textAnchor="end" fill="#64748b" fontSize="10">
                {formatUnits(tick, locale)}
              </text>
            </g>
          );
        })}
        <line x1={left} y1={top + chartHeight} x2={width - right} y2={top + chartHeight} stroke="#cbd5e1" />
        {periods.map((period, index) => {
          const x = left + index * slot;
          return (
            <rect
              key={`${period}:hit`}
              x={x}
              y={top}
              width={slot}
              height={chartHeight}
              fill="#117f8a"
              opacity="0"
              className="cursor-pointer transition-opacity hover:opacity-[0.08]"
              onClick={() => onTogglePeriod(period)}
            />
          );
        })}
        {bars.map((bar) => (
          <rect
            key={bar.key}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            fill={bar.color}
            rx="2"
            className={`cursor-pointer transition-opacity hover:opacity-80 ${
              activeArticleIds.length > 0 && !activeArticleIds.includes(bar.article.articleId)
                ? 'opacity-[0.45]'
                : 'opacity-100'
            }`}
            onClick={(event) => {
              event.stopPropagation();
              onToggleArticle(bar.article.articleId);
            }}
          >
            <title>{`${customerSkuDisplay(bar.article, copy)} - ${formatUnits(unitsByPeriodArticle.get(bar.key) ?? 0, locale)} Units`}</title>
          </rect>
        ))}
        {periods.map((period, index) => {
          const x = left + index * slot;
          const isActive = activePeriod?.from === periodFilter(period, granularity, locale, copy).from;
          if (!isActive) return null;
          return (
            <rect
              key={`${period}:active`}
              x={x}
              y={top}
              width={slot}
              height={chartHeight}
              fill="transparent"
              stroke="#117f8a"
              strokeWidth="2"
              pointerEvents="none"
            />
          );
        })}
        {periods.map((period, index) => {
          if (periods.length > 10 && index % Math.ceil(periods.length / 8) !== 0 && index !== periods.length - 1) {
            return null;
          }
          const x = left + index * slot + slot / 2;
          return (
            <text
              key={period}
              x={x}
              y={height - 10}
              textAnchor="middle"
              fill="#64748b"
              fontSize="10"
              role="button"
              tabIndex={0}
              className="cursor-pointer transition-colors hover:fill-[#117f8a]"
              onClick={() => onTogglePeriod(period)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onTogglePeriod(period);
                }
              }}
            >
              {formatPeriodAxis(period, granularity, locale)}
            </text>
          );
        })}
      </svg>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {articles.map((article, index) => {
          const active = activeArticleIds.includes(article.articleId);
          return (
            <button
              key={article.articleId}
              type="button"
              onClick={() => onToggleArticle(article.articleId)}
              className={`flex min-w-0 items-center gap-2 rounded-md px-2 py-1 text-left text-xs ${
                active ? 'bg-[#e5f6f7] text-[#0c6871]' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: articleColorById.get(article.articleId) ?? chartColor(index) }}
              />
              <span className="truncate">{customerSkuDisplay(article, copy)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BarList({
  rows,
  copy,
  isActive,
  onToggle,
}: {
  rows: Array<{ key: string; label: string; value: number; detail: string }>;
  copy: DashboardCopy;
  isActive: (key: string) => boolean;
  onToggle: (key: string) => void;
}) {
  if (rows.length === 0) return <EmptyState text={copy.noDataInPeriod} />;
  const max = Math.max(1, ...rows.map((row) => row.value));
  return (
    <div className="space-y-2">
      {rows.slice(0, 12).map((row) => {
        const active = isActive(row.key);
        return (
          <button
            key={row.key}
            type="button"
            onClick={() => onToggle(row.key)}
            className={`block w-full rounded-md px-2 py-2 text-left transition ${
              active ? 'bg-[#e5f6f7]' : 'hover:bg-slate-50'
            }`}
          >
            <div className="mb-1 flex items-center justify-between gap-3 text-sm">
              <span className="truncate font-medium">{row.label}</span>
              <span className="shrink-0 text-xs text-slate-500">{row.detail}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className={`h-full rounded-full ${active ? 'bg-[#0c6871]' : 'bg-[#117f8a]'}`}
                style={{ width: `${Math.max(4, (row.value / max) * 100)}%` }}
              />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ArticleTable({
  rows,
  locale,
  copy,
  activeArticleIds,
  onToggle,
}: {
  rows: ArticleSalesRow[];
  locale: Locale;
  copy: DashboardCopy;
  activeArticleIds: string[];
  onToggle: (articleId: string) => void;
}) {
  if (rows.length === 0) return <EmptyState text={copy.noProducts} />;
  return (
    <div className="max-h-[440px] overflow-auto">
      <table className="w-full min-w-[680px] text-left text-sm">
        <thead className="sticky top-0 bg-white text-xs uppercase text-slate-500">
          <tr>
            <th className="border-b border-slate-200 px-3 py-2">{copy.customerSkuArticle}</th>
            <th className="border-b border-slate-200 px-3 py-2 text-right">{copy.sold}</th>
            <th className="border-b border-slate-200 px-3 py-2 text-right">{copy.stock}</th>
            <th className="border-b border-slate-200 px-3 py-2 text-right">{copy.cover}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const active = activeArticleIds.includes(row.articleId);
            return (
              <tr
                key={row.articleId}
                onClick={() => onToggle(row.articleId)}
                className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${
                  active ? 'bg-[#e5f6f7]' : ''
                }`}
              >
                <td className="px-3 py-2">
                  <span className="block font-medium">{customerSkuDisplay(row, copy)}</span>
                  <span className="block max-w-[360px] truncate text-xs text-slate-500">
                    {articleSubtitle(row, copy)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-medium">{formatUnits(row.units, locale)}</td>
                <td className="px-3 py-2 text-right font-medium">{formatUnits(row.quantityInStock, locale)}</td>
                <td className="px-3 py-2 text-right">{formatCoverDays(row.coverDays30d, locale, copy)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-1 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
      {label}
      {children}
    </label>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex min-h-32 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 text-center text-sm text-slate-500">
      {text}
    </div>
  );
}

function createFilterLabels(data: DashboardSalesResponse | undefined, copy: DashboardCopy) {
  const articles = new Map<string, string>();
  for (const row of data?.topArticles ?? []) articles.set(row.articleId, customerSkuDisplay(row, copy));
  for (const row of data?.articleTrend ?? []) articles.set(row.articleId, customerSkuDisplay(row, copy));
  const channels = new Map((data?.channels ?? []).map((row) => [row.channelGroup, row.channelLabel]));
  const countries = new Map((data?.countries ?? []).map((row) => [row.countryCode, countryLabel(row, copy)]));
  return { articles, channels, countries };
}

function countryLabel(row: CountryPoint, copy: DashboardCopy): string {
  if (!row.countryCode || row.countryCode === 'unknown') return copy.unknown;
  return row.countryCode;
}

function customerSkuDisplay(article: {
  articleId?: string | null;
  articleNumber?: string | null;
  customerSku?: string | null;
}, copy: DashboardCopy): string {
  return normalizedText(article.customerSku) || copy.customerSkuMissing;
}

function articleSubtitle(article: {
  articleNumber?: string | null;
  articleName?: string | null;
}, _copy: DashboardCopy): string {
  const internalSku = normalizedText(article.articleNumber);
  const title = normalizedText(article.articleName);
  return [internalSku, title].filter(Boolean).join(' - ') || '-';
}

function normalizedText(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
}

function chartColor(index: number): string {
  return chartColors[index % chartColors.length] ?? '#117f8a';
}

function lastDaysRange(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return { from: isoDate(from), to: isoDate(to) };
}

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function isoDateUtc(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
}

function addDaysIso(value: string, days: number): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function periodFilter(value: string, granularity: Granularity, locale: Locale, copy: DashboardCopy): PeriodFilter {
  const from = value.slice(0, 10);
  if (granularity === 'month') {
    const date = new Date(`${from}T00:00:00.000Z`);
    const to = isoDateUtc(date.getUTCFullYear(), date.getUTCMonth() + 1, 0);
    return { from, to, label: formatPeriodAxis(from, granularity, locale) };
  }
  if (granularity === 'week') {
    const to = addDaysIso(from, 6);
    return { from, to, label: `${formatDate(from, locale)} - ${formatDate(to, locale)}` };
  }
  return { from, to: from, label: formatDate(from, locale) };
}

function niceChartMax(value: number): number {
  if (value <= 1) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const normalized = value / power;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * power;
}

function buildTicks(max: number): number[] {
  return Array.from(new Set([0, 0.25, 0.5, 0.75, 1].map((part) => Math.round(max * part))));
}

function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);
}

function formatUnits(value: number, locale: Locale): string {
  if (value >= 100_000) {
    return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  }
  return formatNumber(value, locale);
}

function formatRevenue(value: number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number | null, locale: Locale): string {
  if (value === null) return '-';
  return `${value > 0 ? '+' : ''}${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)}%`;
}

function formatCoverDays(value: number | null | undefined, locale: Locale, copy: DashboardCopy): string {
  if (value === null || value === undefined) return '-';
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${copy.days}`;
}

function formatDate(value: string | null | undefined, locale: Locale): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: '2-digit' }).format(
    new Date(`${value.slice(0, 10)}T00:00:00.000Z`),
  );
}

function formatPeriodAxis(value: string, granularity: Granularity, locale: Locale): string {
  if (!value) return '-';
  const date = new Date(`${value.slice(0, 10)}T00:00:00.000Z`);
  if (granularity === 'month') {
    return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date).replace(/\.$/, '');
  }
  if (granularity === 'week') {
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' }).format(date).replace(/\.$/, '');
  }
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(date);
}
